import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { IPagedResult } from '../../shared/models/paged-result';
import {
  Promotion,
  CreatePromotionCommand,
  UpdatePromotionCommand,
  PromotionCheckResponse,
  PromotionDetailResponse,
  PromotionFilterSet,
} from '../../shared/models/sale/promotion.model';
import { serialize } from '../../utility/serialize';
import { DistributionChannel } from './catalog';

export const promotionApi = createApi({
  reducerPath: 'promotionApi',
  baseQuery,
  tagTypes: ['Promotion', 'Promotions'],
  endpoints: (builder) => ({
    getPromotions: builder.query<IPagedResult<Promotion>, PromotionFilterSet | string | void>({
      query: (filterSet) => {
        let qs = '';
        if (typeof filterSet === 'object') {
          qs = `?${serialize(filterSet)}`;
        } else if (typeof filterSet === 'string') {
          qs = `?${filterSet}`;
        }
        return {
          url: `/api/promotions${qs}`,
          method: 'GET',
        };
      },
      providesTags: ['Promotions'],
    }),
    getPromotionById: builder.query<Promotion, string>({
      query: (id) => ({
        url: `/api/promotions/${id}`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_, __, id) => [{ type: 'Promotion', id: id.split('?')[0] }],
    }),
    getMarketPromotionById: builder.query<
      Promotion,
      { promotionId: string; marketId: string; channel?: DistributionChannel }
    >({
      query: ({ promotionId, marketId, channel }) => {
        return {
          url: `/api/markets/${marketId}/promotions/${promotionId}?channel=${channel?.toString() ?? ''}`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: (_, __, { promotionId }) => [{ type: 'Promotion', id: promotionId }],
    }),
    createPromotion: builder.mutation<Promotion, CreatePromotionCommand>({
      query: (promotion) => {
        return {
          url: '/api/promotions',
          method: 'POST',
          body: promotion,
        };
      },
      transformResponse: (response: Promotion) => response,
      transformErrorResponse: (error) => error,
      invalidatesTags: ['Promotions'],
    }),
    updatePromotion: builder.mutation<Promotion, UpdatePromotionCommand>({
      query: ({ id, ...promotion }) => {
        return {
          url: `/api/promotions/${id}`,
          method: 'PUT',
          body: promotion,
        };
      },
      transformResponse: (response: Promotion) => response,
      transformErrorResponse: (error) => error,
      invalidatesTags: (_, __, { id }) => [{ type: 'Promotion', id }, 'Promotions'],
    }),
    deletePromotion: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/promotions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => ['Promotions'],
    }),
    getPromotionSuggestions: builder.query<
      PromotionCheckResponse,
      { materialCode: string; companyId: string; marketId?: string; channel?: DistributionChannel }
    >({
      query: ({ materialCode, companyId, marketId, channel }) => {
        const params = new URLSearchParams();
        params.append('materialCode', materialCode);
        params.append('companyId', companyId);

        if (channel) {
          params.append('channel', channel.toString());
        }

        if (marketId) {
          params.append('marketId', marketId);
        }
        return {
          url: `/api/promotions/suggestions?${params.toString()}`,
          method: 'GET',
        };
      },
    }),
    getPromotionDetails: builder.query<PromotionDetailResponse, string>({
      query: (promotionId) => ({
        url: `/api/promotions/${promotionId}/details`,
        method: 'GET',
      }),
    }),
    getCrossPromotionSuggestions: builder.query<
      PromotionCheckResponse[], // ✅ Ahora retorna array
      {
        materialCode: string;
        marketId: string;
        companyId?: string;
        lot?: string;
        vin?: string;
        vehicleBrand?: string;
        vehicleModel?: string;
        vehicleColor?: string;
        vehicleYear?: number;
      }
    >({
      query: ({
        materialCode,
        marketId,
        companyId,
        lot,
        vin,
        vehicleBrand,
        vehicleModel,
        vehicleColor,
        vehicleYear,
      }) => {
        const params = new URLSearchParams();
        params.append('materialCode', materialCode);
        params.append('marketId', marketId);
        if (companyId) {
          params.append('companyId', companyId);
        }
        if (vin) {
          params.append('vin', vin);
        }
        if (!vin && lot) {
          params.append('lot', lot);
        }
        // Agregar información del vehículo si está disponible
        if (vehicleBrand) {
          params.append('vehicleBrand', vehicleBrand);
        }
        if (vehicleModel) {
          params.append('vehicleModel', vehicleModel);
        }
        if (vehicleColor) {
          params.append('vehicleColor', vehicleColor);
        }
        if (vehicleYear) {
          params.append('vehicleYear', vehicleYear.toString());
        }
        const fullUrl = `/api/promotions/suggestioncross?${params.toString()}`;
        return {
          url: fullUrl,
          method: 'GET',
        };
      },
    }),
  }),
});

export const {
  useGetPromotionsQuery,
  useLazyGetPromotionsQuery,
  useGetPromotionByIdQuery,
  useLazyGetPromotionByIdQuery,
  useGetMarketPromotionByIdQuery,
  useLazyGetMarketPromotionByIdQuery,
  useCreatePromotionMutation,
  useUpdatePromotionMutation,
  useDeletePromotionMutation,
  useGetPromotionSuggestionsQuery,
  useLazyGetPromotionSuggestionsQuery,
  useGetPromotionDetailsQuery,
  useLazyGetPromotionDetailsQuery,
  useGetCrossPromotionSuggestionsQuery,
  useLazyGetCrossPromotionSuggestionsQuery,
} = promotionApi;
